// src/pages/student-portal/certificates/CertificateDetailPage.js
// Student Portal version (purple theme) - ported from customer-dashboard certificate detail

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Button,
    Chip,
    Skeleton,
    CircularProgress,
    Alert,
    Avatar,
    IconButton,
    Snackbar,
} from '@mui/material';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#8b5cf6';

function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

export default function CertificateDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [certificate, setCertificate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchCertificate = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get(`/api/lms/portal/certificates/${id}`);
            const data = response.data?.certificate || response.data;
            setCertificate(data);
        } catch (err) {
            console.error('Failed to fetch certificate:', err);
            setError(err.response?.data?.message || 'Failed to load certificate details.');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchCertificate();
    }, [fetchCertificate]);

    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const handleCopyNumber = useCallback(() => {
        const certNumber = certificate?.certificate_number;
        if (certNumber && navigator.clipboard) {
            navigator.clipboard.writeText(certNumber);
            showSnackbar('Certificate number copied to clipboard!');
        }
    }, [certificate, showSnackbar]);

    const handleDownload = useCallback(async () => {
        setDownloading(true);
        try {
            const response = await axiosInstance.get(`/api/lms/portal/certificates/${id}/download`);
            const url = response.data?.download_url;
            if (url) {
                window.open(url, '_blank');
            } else {
                showSnackbar('Download not available at this time.', 'info');
            }
        } catch (err) {
            console.error('Failed to download certificate:', err);
            showSnackbar(err.response?.data?.message || 'Failed to download certificate.', 'error');
        } finally {
            setDownloading(false);
        }
    }, [id, showSnackbar]);

    const handleShare = useCallback(() => {
        const shareUrl = certificate?.verification_url || window.location.href;
        const courseTitle = certificate?.course?.title || 'Course';
        if (navigator.share) {
            navigator.share({
                title: `Certificate of Completion - ${courseTitle}`,
                text: `I have successfully completed ${courseTitle}!`,
                url: shareUrl,
            }).catch(() => {});
        } else if (navigator.clipboard) {
            navigator.clipboard.writeText(shareUrl);
            showSnackbar('Share link copied to clipboard!');
        }
    }, [certificate, showSnackbar]);

    const handleVerify = useCallback(() => {
        const verifyUrl = certificate?.verification_url;
        if (verifyUrl) {
            window.open(verifyUrl, '_blank');
        } else {
            showSnackbar('Verification link not available.', 'info');
        }
    }, [certificate, showSnackbar]);

    // Loading state with skeletons
    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: 4, px: { xs: 2, md: 4 } }}>
                <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                    <Skeleton
                        variant="rounded"
                        width={180}
                        height={36}
                        sx={{ borderRadius: '8px', mb: 3 }}
                    />
                    <Skeleton
                        variant="rounded"
                        height={480}
                        sx={{ borderRadius: '20px', mb: 3 }}
                    />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Skeleton variant="rounded" height={160} sx={{ borderRadius: '16px' }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Skeleton variant="rounded" height={160} sx={{ borderRadius: '16px' }} />
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
                <Alert
                    severity="error"
                    sx={{ borderRadius: '12px', fontFamily, mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={fetchCertificate}
                            sx={{ fontFamily, textTransform: 'none' }}
                        >
                            Retry
                        </Button>
                    }
                >
                    {error}
                </Alert>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/student-portal/certificates')}
                    sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}
                >
                    Back to Certificates
                </Button>
            </Box>
        );
    }

    if (!certificate) return null;

    const student = certificate.student || {};
    const course = certificate.course || {};
    const enrollment = certificate.enrollment || {};
    const isValid = certificate.is_valid !== false;
    const isExpired = certificate.expires_at && new Date(certificate.expires_at) < new Date();
    const statusValid = isValid && !isExpired;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: 4, px: { xs: 2, md: 4 } }}>
            {/* Back button */}
            <Box sx={{ maxWidth: 900, mx: 'auto', mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/student-portal/certificates')}
                    sx={{
                        fontFamily,
                        fontWeight: 600,
                        textTransform: 'none',
                        color: '#64748b',
                        borderRadius: '10px',
                        px: 1.5,
                        '&:hover': { bgcolor: '#f1f5f9' },
                    }}
                >
                    Back to Certificates
                </Button>
            </Box>

            {/* Certificate Display Card */}
            <Paper
                elevation={0}
                sx={{
                    maxWidth: 900,
                    mx: 'auto',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    border: '1px solid #e2e8f0',
                    mb: 3,
                }}
            >
                {/* Decorative Header */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)',
                        position: 'relative',
                        py: { xs: 5, md: 6 },
                        px: 3,
                        textAlign: 'center',
                        overflow: 'hidden',
                    }}
                >
                    {/* Cross pattern overlay */}
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            opacity: 0.08,
                            backgroundImage:
                                'radial-gradient(circle, #fff 1px, transparent 1px)',
                            backgroundSize: '24px 24px',
                            pointerEvents: 'none',
                        }}
                    />
                    {/* Large Award Icon */}
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,255,255,0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2.5,
                            backdropFilter: 'blur(8px)',
                            border: '2px solid rgba(255,255,255,0.2)',
                        }}
                    >
                        <MilitaryTechIcon sx={{ fontSize: 44, color: '#fff' }} />
                    </Box>
                    <Typography
                        sx={{
                            fontFamily,
                            fontWeight: 800,
                            fontSize: { xs: '1.5rem', md: '1.85rem' },
                            color: '#fff',
                            letterSpacing: 1.5,
                            textTransform: 'uppercase',
                            mb: 1,
                            position: 'relative',
                        }}
                    >
                        Certificate of Completion
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily,
                            fontSize: '1rem',
                            color: 'rgba(255,255,255,0.8)',
                            fontWeight: 500,
                            position: 'relative',
                        }}
                    >
                        This certifies that
                    </Typography>
                </Box>

                {/* Certificate Body */}
                <Box sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
                    {/* Student full name */}
                    <Typography
                        sx={{
                            fontFamily,
                            fontWeight: 800,
                            fontSize: { xs: '1.6rem', md: '2.1rem' },
                            color: '#1e293b',
                            mb: 0.5,
                            lineHeight: 1.3,
                        }}
                    >
                        {student.full_name || 'Student'}
                    </Typography>

                    <Typography
                        sx={{
                            fontFamily,
                            color: '#64748b',
                            fontSize: '0.95rem',
                            mb: 1.5,
                        }}
                    >
                        has successfully completed
                    </Typography>

                    {/* Course title */}
                    <Typography
                        sx={{
                            fontFamily,
                            fontWeight: 700,
                            fontSize: { xs: '1.15rem', md: '1.35rem' },
                            color: ACCENT,
                            mb: 1.5,
                        }}
                    >
                        {course.title || 'Course'}
                    </Typography>

                    {/* Category chip */}
                    {course.category && (
                        <Chip
                            label={course.category}
                            size="small"
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                bgcolor: '#f5f3ff',
                                color: ACCENT,
                                border: '1px solid #ede9fe',
                                mb: 3,
                            }}
                        />
                    )}

                    {/* Details Grid - 3 columns */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: certificate.expires_at ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
                            },
                            gap: 2,
                            maxWidth: 600,
                            mx: 'auto',
                            mb: 3,
                            mt: course.category ? 0 : 3,
                        }}
                    >
                        {/* Issue Date */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: '12px',
                                bgcolor: '#f8fafc',
                                border: '1px solid #f1f5f9',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    justifyContent: 'center',
                                    mb: 0.5,
                                }}
                            >
                                <CalendarTodayIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                <Typography
                                    variant="caption"
                                    sx={{ fontFamily, color: '#94a3b8', fontWeight: 600 }}
                                >
                                    Issue Date
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily,
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    fontSize: '0.9rem',
                                }}
                            >
                                {formatDate(certificate.issued_at)}
                            </Typography>
                        </Paper>

                        {/* Expiry Date (conditional) */}
                        {certificate.expires_at && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: '12px',
                                    bgcolor: '#f8fafc',
                                    border: '1px solid #f1f5f9',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        justifyContent: 'center',
                                        mb: 0.5,
                                    }}
                                >
                                    <CalendarTodayIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                    <Typography
                                        variant="caption"
                                        sx={{ fontFamily, color: '#94a3b8', fontWeight: 600 }}
                                    >
                                        Expiry Date
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily,
                                        fontWeight: 700,
                                        color: isExpired ? '#ef4444' : '#1e293b',
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    {formatDate(certificate.expires_at)}
                                </Typography>
                            </Paper>
                        )}

                        {/* Status */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: '12px',
                                bgcolor: statusValid ? '#f0fdf4' : '#fef2f2',
                                border: `1px solid ${statusValid ? '#dcfce7' : '#fee2e2'}`,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    justifyContent: 'center',
                                    mb: 0.5,
                                }}
                            >
                                {statusValid ? (
                                    <CheckCircleIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                                ) : (
                                    <CancelIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                                )}
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontFamily,
                                        color: statusValid ? '#16a34a' : '#dc2626',
                                        fontWeight: 600,
                                    }}
                                >
                                    Status
                                </Typography>
                            </Box>
                            <Typography
                                sx={{
                                    fontFamily,
                                    fontWeight: 700,
                                    color: statusValid ? '#15803d' : '#b91c1c',
                                    fontSize: '0.9rem',
                                }}
                            >
                                {statusValid ? 'Valid' : 'Expired'}
                            </Typography>
                        </Paper>
                    </Box>

                    {/* Certificate number with copy */}
                    <Paper
                        elevation={0}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2.5,
                            py: 1,
                            borderRadius: '12px',
                            bgcolor: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            mb: 3,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ fontFamily, color: '#94a3b8', fontWeight: 600 }}
                        >
                            Certificate No.
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily,
                                fontWeight: 700,
                                color: '#475569',
                                fontFeatureSettings: '"tnum"',
                                letterSpacing: 0.5,
                            }}
                        >
                            {certificate.certificate_number || 'N/A'}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleCopyNumber}
                            sx={{
                                color: '#94a3b8',
                                '&:hover': { color: ACCENT, bgcolor: '#f5f3ff' },
                            }}
                        >
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Paper>

                    {/* Action Buttons */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 1.5,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={
                                downloading ? (
                                    <CircularProgress size={18} sx={{ color: '#fff' }} />
                                ) : (
                                    <DownloadIcon />
                                )
                            }
                            onClick={handleDownload}
                            disabled={downloading}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                bgcolor: ACCENT,
                                borderRadius: '12px',
                                px: 3,
                                py: 1.2,
                                boxShadow: '0 4px 14px rgba(139, 92, 246, 0.3)',
                                '&:hover': {
                                    bgcolor: '#7c3aed',
                                    boxShadow: '0 6px 20px rgba(139, 92, 246, 0.4)',
                                },
                                '&.Mui-disabled': {
                                    bgcolor: '#c4b5fd',
                                    color: '#fff',
                                },
                            }}
                        >
                            {downloading ? 'Downloading...' : 'Download PDF'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ShareIcon />}
                            onClick={handleShare}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                borderColor: '#e2e8f0',
                                color: '#475569',
                                borderRadius: '12px',
                                px: 3,
                                py: 1.2,
                                '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: '#faf5ff' },
                            }}
                        >
                            Share
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<OpenInNewIcon />}
                            onClick={handleVerify}
                            sx={{
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                borderColor: '#e2e8f0',
                                color: '#475569',
                                borderRadius: '12px',
                                px: 3,
                                py: 1.2,
                                '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: '#faf5ff' },
                            }}
                        >
                            Verify Online
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Additional Info Grid - 2 columns */}
            <Grid container spacing={3} sx={{ maxWidth: 900, mx: 'auto' }}>
                {/* Certificate Holder Card */}
                <Grid item xs={12} md={6}>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            height: '100%',
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '8px',
                                        bgcolor: '#f5f3ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <PersonIcon sx={{ fontSize: 18, color: ACCENT }} />
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily,
                                        fontWeight: 700,
                                        fontSize: '0.95rem',
                                        color: '#1e293b',
                                    }}
                                >
                                    Certificate Holder
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <Avatar
                                    src={student.profile_image_url}
                                    sx={{
                                        width: 52,
                                        height: 52,
                                        bgcolor: '#f5f3ff',
                                        color: ACCENT,
                                        fontFamily,
                                        fontWeight: 700,
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    {student.full_name
                                        ? student.full_name
                                              .split(' ')
                                              .map((n) => n[0])
                                              .join('')
                                              .toUpperCase()
                                              .slice(0, 2)
                                        : <PersonIcon />}
                                </Avatar>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily,
                                            fontWeight: 700,
                                            color: '#1e293b',
                                            fontSize: '0.95rem',
                                            mb: 0.25,
                                        }}
                                    >
                                        {student.full_name || 'Student'}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily,
                                            color: '#64748b',
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        {student.email || ''}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Course Information Card */}
                <Grid item xs={12} md={6}>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            height: '100%',
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2.5,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '8px',
                                        bgcolor: '#f5f3ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <AutoStoriesIcon sx={{ fontSize: 18, color: ACCENT }} />
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily,
                                        fontWeight: 700,
                                        fontSize: '0.95rem',
                                        color: '#1e293b',
                                    }}
                                >
                                    Course Information
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 48,
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        bgcolor: '#f1f5f9',
                                        flexShrink: 0,
                                    }}
                                >
                                    {course.thumbnail_url ? (
                                        <Box
                                            component="img"
                                            src={course.thumbnail_url}
                                            alt={course.title}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <AutoStoriesIcon
                                                sx={{ fontSize: 24, color: '#cbd5e1' }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontFamily,
                                            fontWeight: 700,
                                            color: '#1e293b',
                                            fontSize: '0.95rem',
                                            mb: 0.25,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {course.title || 'Course'}
                                    </Typography>
                                    {enrollment.progress_percentage != null && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 0.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    height: 6,
                                                    borderRadius: '3px',
                                                    bgcolor: '#f1f5f9',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: `${Math.min(enrollment.progress_percentage, 100)}%`,
                                                        height: '100%',
                                                        borderRadius: '3px',
                                                        bgcolor: ACCENT,
                                                        transition: 'width 0.4s ease',
                                                    }}
                                                />
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontFamily,
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    color: '#64748b',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {Math.round(enrollment.progress_percentage)}%
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            {course.course_identity && (
                                <Button
                                    size="small"
                                    onClick={() =>
                                        navigate(
                                            `/student-portal/courses/${course.course_identity}`
                                        )
                                    }
                                    sx={{
                                        fontFamily,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        color: ACCENT,
                                        mt: 2,
                                        px: 0,
                                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                                    }}
                                >
                                    View Course
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ fontFamily, borderRadius: '12px' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
